/**
 * external dependencies
 */
import { TabulatorFull, Options, CellComponent } from 'tabulator-tables';

/**
 * internal dependencies
 */
import { TRANSACTION_FITLER } from '../../types/types';
import { dropIcon, formatDate } from '../../helpers/helpers';
import { heartBeatTableFilter } from './tableFilters/heartbeat-filter';
import { ensureDateRangeWithin31Days, setDateFilter } from './tableHelpers/comman-helpers';

import { IHeartbeat } from '../../models/heartbeat.interface';
import GetHeartBeat from '../../routes/get-heartbeat';

/**
 * function that handles the heart beat table
 * 
 * @returns void
 */
export const heartbeatListTable = async () => {
  const heartbeatTable = document.querySelector('.aafp_heartbeat-table') as HTMLElement;
  if ( !heartbeatTable ) return;

  let startDate = setDateFilter(TRANSACTION_FITLER.dateToSetTwo);
  TRANSACTION_FITLER.startDate.valueAsDate = startDate;

  let endDate = new Date(new Date().setUTCHours(23, 59, 59, 0));
  TRANSACTION_FITLER.endDate.valueAsDate = endDate;
  TRANSACTION_FITLER.endDate.max = new Date().toISOString().split('T')[0];

  TRANSACTION_FITLER.startDate.addEventListener('change', () => {
    const [adjustedStartDate, adjustedEndDate] = ensureDateRangeWithin31Days(new Date(TRANSACTION_FITLER.startDate.value), endDate, 2);
    TRANSACTION_FITLER.endDate.valueAsDate = adjustedEndDate;
    TRANSACTION_FITLER.endDate.max = new Date(adjustedEndDate).toISOString().split('T')[0];
  });

  dropIcon('.aafp_filter-header');

  const { body } = await GetHeartBeat( startDate, endDate, '');

  let tableObject = displayHeartBeatTable( heartbeatTable, body );

  heartBeatTableFilter(tableObject);
}

/**
 * Function handles parsing and displaying data for table
 * 
 * @param {HTMLElement} table the terminal heartbeat table container element
 * @param {object} data the heartbeat data
 * 
 * @returns {TabulatorFull} the table instance
 */
const displayHeartBeatTable = ( table: HTMLElement, data: IHeartbeat[] ): TabulatorFull => {

  /**
   * Formats the date into a more human readable format.
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted date
   */
  const dateFormat = ( cell: CellComponent, formatterParams: any) => {
    const { timeStampReq } = cell.getData() as IHeartbeat;
    return formatDate(timeStampReq);
  }

  /**
   * Displays based upon results
   * 
   * @param {CellComponent} cell the table cell data
   * 
   * @returns the formatted date
   */
  const resultsFormat = (cell: CellComponent, formatterParams: any) => {
    const { result } = cell.getData() as IHeartbeat;
    const status = result === '0' ? 'Active' : result;
    return `<span class="terminal-${result === '0' ? 'active' : 'inactive'}">${status}</span>`;
  }

  const options: Options = {
    index: "ID",
    data: data,
    pagination: true,
    paginationMode: "local",
    paginationSize: 10,
    paginationSizeSelector: [25, 50, 100],
    // @ts-ignore
    paginationCounter: "rows",
    layoutColumnsOnNewData: true,
    selectableRows: false,
    columns: [
      { 
        title: 'Site Code', 
        field: 'terminalId', 
        cssClass: 'aafp_column- aafp_table-font-size',
        sorter: "string",
        resizable: true, 
        vertAlign: 'middle' 
      },
      { 
        title: 'Name', 
        field: 'terminalName', 
        sorter: "string", 
        resizable: true, 
        vertAlign: 'middle' 
      },
      { 
        title: 'Time Stamp', 
        field: 'timeStampReq', 
        sorter: "string", 
        formatter: dateFormat, 
        resizable: true, 
        vertAlign: 'middle' 
      },
      { 
        title: 'Result', 
        field: 'result', 
        formatter: resultsFormat,
        sorter: "string", 
        cssClass: "aafp_column-button-wide",
        resizable: false,
        width: 30, 
        vertAlign: 'middle' 
      }
    ]
  };

  const heartBeatTableGrid = new TabulatorFull( table, options);

  return heartBeatTableGrid;
}